import type { ElementType, ReactNode } from 'react';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UserNameCell } from '@/components/core';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui-kit/empty';
import { Skeleton } from '@/components/ui-kit/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';
import { type TableDensity, useTableDensity } from '@/hooks/use-table-density';
import { cn } from '@/lib/utils';
import { dateOnlyOf, formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import {
  LeaveCalendarEmployeeGroup,
  LeaveCalendarHolidayEvent,
  LeaveCalendarLeaveEvent,
} from '../../types/leave-calendar.types';
import { PersonAvatar } from '@/components/core/person-avatar/person-avatar';
import type { Presence } from '@/components/core/presence-avatar/presence-avatar';
import { daySpanOf, formatDayLabel } from '../../utils/leave-calendar-week';

interface LeaveCalendarGridProps {
  /** The days shown as columns ("YYYY-MM-DD"): a week, or a whole month as a compact timeline. */
  days: string[];
  /** Today in the organization's time zone, highlighted when it is one of `days`. */
  today: string;
  employees: LeaveCalendarEmployeeGroup[];
  holidays: LeaveCalendarHolidayEvent[];
  isLoading: boolean;
  /** Shown when no one is on leave in `days`. */
  emptyMessage: string;
  /** What shows under the name by IAM user id (a group's `ItemId`): the department, else the email. */
  subtitleOf?: (userId: string) => string | undefined;
  /** Whether `day` is a weekend for this person (their HR term schedules no hours on it); shaded. */
  weekendOf?: (userId: string, day: string) => boolean;
  /** Today's presence per person, for the avatars (as People shows it); none for a viewer who may not read it. */
  presenceOf?: (userId: string) => { presence?: Presence; presenceLabel?: string };
  /** Makes each bar a button; called with the leave and whose it is. */
  onOpenLeave?: (leave: LeaveCalendarLeaveEvent, employee: LeaveCalendarEmployeeGroup) => void;
}

const LEAVE_TYPE_KEYS: Record<string, string> = { FullDay: 'FULL_DAY', HalfDay: 'HALF_DAY' };
const STATUS_KEYS: Record<string, string> = {
  Approved: 'APPROVED',
  Pending: 'PENDING',
  Rejected: 'REJECTED',
};

/** More days than a week turn the grid into a timeline: narrow columns, one-line bars. */
const WEEK_LENGTH = 7;

/** Prototype `.tl`: a 220px name column; a day is 96px wide in a week and 34px in a month. */
const NAME_COLUMN = '220px';
const WEEK_DAY_MIN = '96px';
const MONTH_DAY_MIN = '34px';
/**
 * One lane (a row of bars) follows the row density every table shares (condensed 40px, regular 48px,
 * relaxed 56px); leaves that overlap in time stack in lanes.
 */
const LANE_HEIGHT: Record<TableDensity, number> = { condensed: 40, regular: 48, relaxed: 56 };

const WithTooltip = ({ content, children }: { content?: ReactNode; children: ReactNode }) =>
  content ? (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{content}</TooltipContent>
    </Tooltip>
  ) : (
    children
  );

/** The bar's colours by status (prototype `.lb-approved`, `.lb-pending`, `.lb-rejected`). */
const STATUS_BAR: Record<string, string> = {
  Approved: 'border-tint-blue-border bg-accent text-accent-foreground',
  Pending: 'border-warning-border bg-warning-subtle text-warning-subtle-foreground',
  Rejected: 'border-destructive-border bg-destructive-subtle text-destructive-subtle-foreground',
};

interface PlacedLeave {
  leave: LeaveCalendarLeaveEvent;
  from: number;
  to: number;
  lane: number;
}

/**
 * Puts each leave of one person in a lane: a leave takes the first lane whose last bar ended before it
 * starts, so overlapping leaves stack instead of covering each other (prototype `tl-row` lanes).
 */
const placeLeaves = (leaves: LeaveCalendarLeaveEvent[], days: string[]) => {
  const placed: PlacedLeave[] = [];
  const laneEnds: number[] = [];
  leaves
    .map((leave) => ({ leave, span: daySpanOf(leave.StartDate, leave.EndDate, days) }))
    .filter(
      (entry): entry is { leave: LeaveCalendarLeaveEvent; span: { from: number; to: number } } =>
        Boolean(entry.span)
    )
    .sort((a, b) => a.span.from - b.span.from)
    .forEach(({ leave, span }) => {
      let lane = laneEnds.findIndex((end) => end < span.from);
      if (lane < 0) {
        lane = laneEnds.length;
        laneEnds.push(span.to);
      } else laneEnds[lane] = span.to;
      placed.push({ leave, from: span.from, to: span.to, lane });
    });
  return { placed, laneCount: Math.max(1, laneEnds.length) };
};

const LeaveBar = ({
  placed,
  days,
  compact,
  onOpen,
}: {
  placed: PlacedLeave;
  days: string[];
  compact: boolean;
  /** Makes the bar a button that opens the leave's details. */
  onOpen?: () => void;
}) => {
  const { t } = useTranslation();
  const { leave, from, to, lane } = placed;
  const typeKey = leave.LeaveType ? LEAVE_TYPE_KEYS[leave.LeaveType] : undefined;
  const statusKey = STATUS_KEYS[leave.Status];
  const range = `${formatDateOnly(leave.StartDate)} - ${formatDateOnly(leave.EndDate)}`;
  const name = leave.LeaveCategory || leave.Title;
  // A leave that began before (or ends after) the shown days runs flat into the edge.
  const continuesLeft = dateOnlyOf(leave.StartDate) < days[0];
  const continuesRight = dateOnlyOf(leave.EndDate) > days[days.length - 1];
  const isHalfDay = leave.LeaveType === 'HalfDay';
  const isRejected = leave.Status === 'Rejected';
  const Bar: ElementType = onOpen ? 'button' : 'div';

  return (
    <WithTooltip
      content={
        <>
          <p className="font-medium">{name}</p>
          <p>{range}</p>
          {typeKey && <p>{t(typeKey)}</p>}
          {statusKey && <p>{t(statusKey)}</p>}
        </>
      }
    >
      <Bar
        {...(onOpen
          ? {
              type: 'button',
              onClick: onOpen,
              'aria-label': `${name}, ${range}${statusKey ? `, ${t(statusKey)}` : ''}`,
            }
          : {})}
        className={cn(
          'flex h-7 min-w-0 items-center self-center rounded-md border px-2 text-xs font-medium',
          onOpen &&
            'cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          STATUS_BAR[leave.Status] ?? STATUS_BAR.Approved,
          compact ? 'mx-px px-1.5' : 'mx-[3px]',
          continuesLeft && 'ml-0 rounded-l-none border-l-0',
          continuesRight && 'mr-0 rounded-r-none border-r-0',
          // Prototype `.tl-bar.half`: the right half is lighter.
          isHalfDay && 'bg-[linear-gradient(90deg,transparent_50%,oklch(var(--card)/0.55)_50%)]',
          'hover:brightness-[.97]'
        )}
        style={{ gridColumn: `${from + 1} / ${to + 2}`, gridRow: lane + 1 }}
      >
        <span className={cn('truncate', isRejected && 'line-through')}>{name}</span>
        {!compact && typeKey && (
          <span className={cn('shrink-0 whitespace-pre', isRejected && 'line-through')}>
            {' · '}
            {t(typeKey)}
          </span>
        )}
      </Bar>
    </WithTooltip>
  );
};

/**
 * Leave over a run of days (prototype `.tl`): one row per employee, one column per day, each leave a
 * bar spanning the days it covers (clipped to the range), overlapping leaves stacked in lanes. Holidays
 * tint their column and are named in its header (in a tooltip on the compact month timeline).
 */
export const LeaveCalendarGrid = ({
  days,
  today,
  employees,
  holidays,
  isLoading,
  emptyMessage,
  subtitleOf,
  weekendOf,
  presenceOf,
  onOpenLeave,
}: LeaveCalendarGridProps) => {
  const { t, i18n } = useTranslation();
  const [density] = useTableDensity();
  const compact = days.length > WEEK_LENGTH;
  const dayColumns = `repeat(${days.length}, minmax(0, 1fr))`;
  const fullRow = { gridColumn: '1 / -1' };

  const holidaysOn = (day: string) =>
    holidays.filter((holiday) => dateOnlyOf(holiday.StartDate) === day);
  // Today's column is a faint wash of the primary colour (prototype `tl-bg.today`), never a solid fill.
  const dayTint = (day: string) =>
    cn(
      day === today && 'bg-[color-mix(in_oklch,oklch(var(--primary))_8%,transparent)]',
      holidaysOn(day).length > 0 && 'bg-muted'
    );

  return (
    <TooltipProvider>
      <div className="overflow-x-auto">
        <div
          className="grid min-w-min"
          role="grid"
          style={{
            // The names column stays in place while the days scroll sideways. A phone gets the mobile view.
            gridTemplateColumns: `${NAME_COLUMN} repeat(${days.length}, minmax(${compact ? MONTH_DAY_MIN : WEEK_DAY_MIN}, 1fr))`,
          }}
        >
          <div className="sticky left-0 z-raised flex min-h-12 items-center border-b border-r bg-card pl-4 pr-3 text-[13px] font-medium text-muted-foreground">
            {t('EMPLOYEES')}
          </div>
          {days.map((day) => {
            const dayHolidays = holidaysOn(day);
            const isToday = day === today;
            return (
              <WithTooltip
                key={day}
                content={
                  compact && dayHolidays.length > 0
                    ? dayHolidays.map((holiday) => <p key={holiday.ItemId}>{holiday.Name}</p>)
                    : undefined
                }
              >
                <div
                  className={cn(
                    'grid min-h-12 min-w-0 place-items-center content-center border-b border-l py-1 text-xs text-muted-foreground',
                    compact && 'text-[11px]',
                    // Today is marked by the primary circle alone (prototype `tl-hd.today`); only
                    // holidays tint the header, as their column below.
                    dayHolidays.length > 0 && 'bg-muted'
                  )}
                >
                  <span className={cn(isToday && 'font-semibold text-primary')}>
                    {formatDayLabel(day, i18n.language, {
                      weekday: compact ? 'narrow' : 'short',
                    })}
                  </span>
                  <b
                    className={cn(
                      'font-medium tabular-nums text-foreground',
                      compact ? 'text-[13px]' : 'text-sm',
                      isToday &&
                        'inline-grid h-6 min-w-6 place-items-center rounded-full bg-primary px-1 text-primary-foreground'
                    )}
                  >
                    {formatDayLabel(day, i18n.language, { day: 'numeric' })}
                  </b>
                  {!compact &&
                    dayHolidays.map((holiday) => (
                      <span key={holiday.ItemId} className="max-w-full truncate px-1">
                        {holiday.Name}
                      </span>
                    ))}
                </div>
              </WithTooltip>
            );
          })}

          {isLoading &&
            Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="border-b p-2" style={fullRow}>
                <Skeleton className="h-10 w-full" />
              </div>
            ))}

          {!isLoading && employees.length === 0 && (
            <div className="border-b" style={fullRow}>
              <Empty className="py-10">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Calendar />
                  </EmptyMedia>
                  <EmptyTitle>{emptyMessage}</EmptyTitle>
                  <EmptyDescription>{t('LEAVE_CALENDAR_EMPTY_HINT')}</EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          )}

          {!isLoading &&
            employees.map((employee) => {
              const { placed, laneCount } = placeLeaves(employee.LeaveEvents, days);
              return (
                <div key={employee.ItemId} className="contents" role="row">
                  <div className="sticky left-0 z-raised flex min-w-0 items-center gap-2.5 border-b border-r bg-card pl-4 pr-3">
                    <PersonAvatar name={employee.DisplayName} {...presenceOf?.(employee.ItemId)} />
                    <UserNameCell
                      name={employee.DisplayName}
                      email={subtitleOf?.(employee.ItemId) ?? employee.Email}
                    />
                  </div>
                  <div
                    className="relative border-b"
                    style={{
                      gridColumn: `span ${days.length}`,
                      minHeight: `${laneCount * LANE_HEIGHT[density]}px`,
                    }}
                  >
                    <div
                      className="absolute inset-0 grid"
                      style={{ gridTemplateColumns: dayColumns }}
                      aria-hidden
                    >
                      {days.map((day) => (
                        <div
                          key={day}
                          className={cn(
                            'border-l',
                            // Each person's own weekend (prototype `tl-bg.wkend`); today and holidays win.
                            // The page background: a step darker than the card in both modes
                            // Light: the page background (`surface-subtle` is invisible on white). Dark: half
                            // way from the card to `surface-subtle`, as faint as the prototype's.
                            weekendOf?.(employee.ItemId, day) &&
                              'bg-background dark:bg-[color-mix(in_oklch,oklch(var(--surface-subtle))_55%,oklch(var(--card)))]',
                            dayTint(day)
                          )}
                        />
                      ))}
                    </div>
                    {/* No padding or gap, so the bars' columns line up with the day columns behind. */}
                    <div
                      className="relative grid"
                      style={{
                        gridTemplateColumns: dayColumns,
                        gridAutoRows: `${LANE_HEIGHT[density]}px`,
                      }}
                    >
                      {placed.map((entry) => (
                        <LeaveBar
                          key={entry.leave.ItemId}
                          placed={entry}
                          days={days}
                          compact={compact}
                          onOpen={
                            onOpenLeave ? () => onOpenLeave(entry.leave, employee) : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </TooltipProvider>
  );
};
